import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { jsPDF } from "jspdf";
import { APP_VERSION } from "../App";
import { getSubscriptionId } from "../lib/onesignal";
import MyReportsModal from "./MyReportsModal";

// Legal content
const privacyPolicy = `
**Privacy Policy for PCOS Insights**
Last updated: March 2025

**1. Information We Collect**
We collect information you provide directly, including:
- Email address for account creation
- Health data you choose to log (hormone values, symptoms, cycle information)
- Usage data to improve our services

**2. How We Use Your Information**
Your data is used to:
- Provide personalized health insights
- Track your symptom and hormone patterns
- Improve our educational content

**3. Data Storage & Security**
- Your data is stored securely using industry-standard encryption
- We use Supabase for secure data management
- Some data is stored locally on your device

**4. Data Sharing**
We do NOT sell or share your personal health data with third parties. Your health information remains private.

**5. Your Rights**
You can:
- Access your data at any time
- Request deletion of your account
- Export your health logs

**6. Contact**
For privacy concerns, contact us through the app.
`;

const termsOfUse = `
**Terms of Use for PCOS Insights**
Last updated: March 2025

**1. Acceptance of Terms**
By using PCOS Insights, you agree to these terms. If you disagree, please discontinue use.

**2. Service Description**
PCOS Insights is an educational health tracking tool designed to help users understand PCOS-related patterns. It is NOT a medical device or diagnostic tool.

**3. User Responsibilities**
- Provide accurate information
- Keep your account credentials secure
- Use the app for personal, non-commercial purposes

**4. Limitations**
- The app provides educational content only
- Results are not medical diagnoses
- Always consult healthcare providers for medical decisions

**5. Intellectual Property**
All content, features, and functionality are owned by PCOS Insights and protected by copyright.

**6. Disclaimer of Warranties**
The service is provided "as is" without warranties of any kind.

**7. Limitation of Liability**
PCOS Insights shall not be liable for any health decisions made based on app content.

**8. Changes to Terms**
We may update these terms. Continued use constitutes acceptance.
`;

const medicalDisclaimer = `
**Medical Disclaimer**

**IMPORTANT: Please Read Carefully**

PCOS Insights is designed for educational and informational purposes only. This application:

❌ Is NOT a medical device
❌ Does NOT provide medical diagnoses
❌ Does NOT replace professional medical advice
❌ Should NOT be used for medical decision-making

**The information provided includes:**
- General educational content about PCOS
- Pattern recognition based on user-entered data
- Reference ranges that may vary by laboratory

**Always consult a qualified healthcare provider for:**
- Diagnosis of any medical condition
- Interpretation of lab results
- Treatment decisions
- Any health concerns

**If you are experiencing a medical emergency, call your local emergency services immediately.**

By using this app, you acknowledge that:
1. You understand this is not medical advice
2. You will consult healthcare providers for medical decisions
3. You take full responsibility for how you use this information

Reference ranges and interpretations are based on commonly cited medical literature but may not apply to your individual situation.
`;

export default function ProfileScreen({ userId, userEmail, onSignOut }) {
  const [user, setUser] = useState({ name: "", email: userEmail || "" });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("Coming soon");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'privacy', 'terms', 'disclaimer'
  const [exporting, setExporting] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("name,email")
        .eq("id", userId)
        .maybeSingle();
      
      if (data) {
        setUser({ name: data.name || "", email: data.email || userEmail || "" });
        setEditName(data.name || "");
      } else {
        // Profile doesn't exist - create it
        await supabase.from("profiles").upsert({
          id: userId,
          email: userEmail,
          name: null,
        });
        setUser({ name: "", email: userEmail || "" });
      }
    };
    fetchProfile();
  }, [userId, userEmail]);

  const getInitials = () => {
    if (!user.name) return "?";
    return user.name.charAt(0).toUpperCase();
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    
    setSaving(true);
    try {
      // Try update first (for existing profile)
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ name: editName.trim() })
        .eq("id", userId);
      
      if (updateError) {
        // If update fails, try insert (for new profile)
        const { error: insertError } = await supabase
          .from("profiles")
          .insert({
            id: userId,
            name: editName.trim(),
            email: user.email,
          });
        
        if (insertError) {
          console.error("Error saving name:", insertError);
          // Check if the error is RLS-related
          if (insertError.code === '42501' || insertError.message?.includes('403') || insertError.message?.includes('denied')) {
            setToastMessage("Unable to save. Please check database permissions.");
          } else {
            setToastMessage("Failed to update name. Please try again.");
          }
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);
          setSaving(false);
          return;
        }
      }
      
      // Success
      setUser(prev => ({ ...prev, name: editName.trim() }));
      setIsEditingName(false);
      setToastMessage("Name updated successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error("Error saving name:", err);
      // Still update locally even if server fails (graceful degradation)
      setUser(prev => ({ ...prev, name: editName.trim() }));
      setIsEditingName(false);
      setToastMessage("Name saved locally. Server sync may be pending.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    }
    setSaving(false);
  };

  const menuItems = ["My Reports", "Symptom History", "Notification Preferences", "About PCOS Insights"];

  const [showMyReports, setShowMyReports] = useState(false);

  const handleMenuClick = (item) => {
    if (item === "My Reports") {
      setShowMyReports(true);
      return;
    }
    setToastMessage("Coming soon");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  // Export Health Report as PDF
  const handleExportReport = async () => {
    setExporting(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;
      
      // Helper function to add text with word wrap
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line, idx) => {
          if (y + (idx * lineHeight) > 270) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, x, y + (idx * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };
      
      // Header
      doc.setFillColor(167, 139, 250);
      doc.rect(0, 0, pageWidth, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text('PCOS Health Report', pageWidth / 2, 18, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`Patient: ${user.name || 'Not specified'}`, pageWidth / 2, 35, { align: 'center' });
      
      yPos = 55;
      doc.setTextColor(0, 0, 0);
      
      // --- CYCLE INFORMATION ---
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(109, 94, 245);
      doc.text('Cycle Information', 15, yPos);
      yPos += 8;
      
      const cycleData = localStorage.getItem('pcosLastPeriod');
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      
      if (cycleData) {
        const cycle = JSON.parse(cycleData);
        const lastPeriod = new Date(cycle.lastPeriodDate);
        const daysSince = Math.floor((new Date() - lastPeriod) / (1000 * 60 * 60 * 24));
        const cycleLen = parseInt(cycle.cycleLength) || 28;
        const currentDay = (daysSince % cycleLen) + 1;
        
        let phase = 'Menstrual';
        if (currentDay > 5 && currentDay <= 13) phase = 'Follicular';
        else if (currentDay > 13 && currentDay <= 16) phase = 'Ovulation';
        else if (currentDay > 16) phase = 'Luteal';
        
        doc.text(`Last Period: ${lastPeriod.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 15, yPos);
        yPos += 6;
        doc.text(`Cycle Length: ${cycleLen} days`, 15, yPos);
        yPos += 6;
        doc.text(`Current Cycle Day: Day ${currentDay}`, 15, yPos);
        yPos += 6;
        doc.text(`Current Phase: ${phase}`, 15, yPos);
        yPos += 6;
        
        const nextPeriod = new Date(lastPeriod.getTime() + cycleLen * 24 * 60 * 60 * 1000);
        doc.text(`Next Expected Period: ${nextPeriod.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 15, yPos);
      } else {
        doc.text('No cycle data recorded.', 15, yPos);
      }
      yPos += 15;
      
      // --- HORMONE RESULTS ---
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(109, 94, 245);
      doc.text('Hormone Results', 15, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      
      const hormoneData = localStorage.getItem('pcosHormones');
      const hormonePattern = localStorage.getItem('pcosHormonePattern');
      
      if (hormoneData) {
        const hormones = JSON.parse(hormoneData);
        
        // Table header
        doc.setFont(undefined, 'bold');
        doc.text('Hormone', 15, yPos);
        doc.text('Value', 70, yPos);
        doc.text('Reference', 110, yPos);
        doc.text('Status', 160, yPos);
        yPos += 6;
        doc.setFont(undefined, 'normal');
        
        // Draw line
        doc.setDrawColor(200, 200, 200);
        doc.line(15, yPos - 2, 195, yPos - 2);
        
        const hormoneConfig = [
          { key: 'lh', label: 'LH', unit: 'mIU/mL', ref: '2-10', getStatus: (v) => v < 2 ? 'Low' : v <= 10 ? 'Normal' : 'Elevated' },
          { key: 'fsh', label: 'FSH', unit: 'mIU/mL', ref: '3-10', getStatus: (v) => v < 3 ? 'Low' : v <= 10 ? 'Normal' : 'Elevated' },
          { key: 'insulin', label: 'Fasting Insulin', unit: 'µIU/mL', ref: '2-12', getStatus: (v) => v <= 8 ? 'Normal' : v <= 12 ? 'Borderline' : 'Elevated' },
          { key: 'testosterone', label: 'Total Testosterone', unit: 'ng/dL', ref: '15-70', getStatus: (v) => v < 15 ? 'Low' : v <= 70 ? 'Normal' : 'Elevated' },
          { key: 'amh', label: 'AMH', unit: 'ng/mL', ref: '1-3.5', getStatus: (v) => v < 1 ? 'Low' : v <= 3.5 ? 'Normal' : 'Elevated' },
        ];
        
        hormoneConfig.forEach(h => {
          const value = parseFloat(hormones[h.key]) || 0;
          const status = h.getStatus(value);
          doc.text(h.label, 15, yPos);
          doc.text(`${value} ${h.unit}`, 70, yPos);
          doc.text(`${h.ref} ${h.unit}`, 110, yPos);
          doc.text(status, 160, yPos);
          yPos += 6;
        });
        
        // LH/FSH Ratio
        if (hormones.lhFshRatio) {
          yPos += 4;
          doc.setFont(undefined, 'bold');
          doc.text(`LH/FSH Ratio: ${hormones.lhFshRatio}`, 15, yPos);
          const ratioStatus = parseFloat(hormones.lhFshRatio) > 2 ? '(May indicate PCOS pattern)' : '(Within normal range)';
          doc.setFont(undefined, 'normal');
          doc.text(ratioStatus, 70, yPos);
        }
        
        // Pattern
        if (hormonePattern) {
          const pattern = JSON.parse(hormonePattern);
          yPos += 8;
          doc.setFont(undefined, 'bold');
          doc.text('Detected Pattern:', 15, yPos);
          doc.setFont(undefined, 'normal');
          doc.text(pattern.pattern, 55, yPos);
        }
      } else {
        doc.text('No hormone data recorded.', 15, yPos);
      }
      yPos += 15;
      
      // --- SYMPTOM HISTORY ---
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(109, 94, 245);
      doc.text('Symptom History (Last 7 Days)', 15, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      
      const symptomKeys = Object.keys(localStorage)
        .filter(k => k.startsWith('pcosSymptoms_'))
        .sort()
        .slice(-7);
      
      if (symptomKeys.length > 0) {
        const severityLabels = ['None', 'Mild', 'Moderate', 'Severe'];
        const symptoms = ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'];
        
        // Table header
        doc.setFont(undefined, 'bold');
        doc.text('Date', 15, yPos);
        symptoms.forEach((s, idx) => {
          const label = s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          doc.text(label.substring(0, 8), 40 + idx * 26, yPos);
        });
        yPos += 6;
        doc.line(15, yPos - 2, 195, yPos - 2);
        doc.setFont(undefined, 'normal');
        
        symptomKeys.forEach(key => {
          const dateStr = key.replace('pcosSymptoms_', '');
          const log = JSON.parse(localStorage.getItem(key) || '{}');
          const displayDate = new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          doc.text(displayDate, 15, yPos);
          symptoms.forEach((s, idx) => {
            const value = log[s] ?? 0;
            doc.text(severityLabels[value], 40 + idx * 26, yPos);
          });
          yPos += 6;
        });
        
        // Symptom pattern
        const symptomPattern = localStorage.getItem('pcosSymptomPattern');
        if (symptomPattern) {
          const pattern = JSON.parse(symptomPattern);
          yPos += 4;
          doc.setFont(undefined, 'bold');
          doc.text('Detected Patterns:', 15, yPos);
          doc.setFont(undefined, 'normal');
          doc.text(pattern.patterns?.join(', ') || 'None identified', 55, yPos);
        }
      } else {
        doc.text('No symptom data recorded.', 15, yPos);
      }
      yPos += 15;
      
      // --- DISCLAIMER ---
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFillColor(254, 243, 199);
      doc.rect(15, yPos, pageWidth - 30, 30, 'F');
      yPos += 8;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(146, 64, 14);
      doc.text('IMPORTANT DISCLAIMER', 20, yPos);
      yPos += 5;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      const disclaimer = 'This report is generated by PCOS Insights for educational purposes only. It is NOT a medical diagnosis. Always consult a qualified healthcare provider for interpretation of results and treatment decisions. Reference ranges may vary by laboratory.';
      yPos = addWrappedText(disclaimer, 20, yPos, pageWidth - 40, 4);
      
      // Footer
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, 285, { align: 'center' });
        doc.text('Generated by PCOS Insights - For Healthcare Provider Review', pageWidth / 2, 290, { align: 'center' });
      }
      
      // Save the PDF
      doc.save(`PCOS_Health_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      
      setToastMessage("Report downloaded successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setToastMessage("Failed to generate report. Please try again.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
    
    setExporting(false);
  };

  return (
    <div className="profile-screen fade-in" data-testid="profile-screen">
      <div className="profile-header" data-testid="profile-header">
        <div className="profile-avatar"><span>{getInitials()}</span></div>
        
        {/* Editable Name Section */}
        {isEditingName ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 280 }}>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter your name"
              className="input-field"
              style={{ textAlign: 'center', fontSize: 16 }}
              data-testid="edit-name-input"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button 
                className="btn-ghost" 
                onClick={() => { setIsEditingName(false); setEditName(user.name || ""); }}
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveName}
                disabled={saving || !editName.trim()}
                style={{ padding: '8px 16px', fontSize: 13 }}
                data-testid="save-name-btn"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div 
              className="profile-name" 
              onClick={() => { setIsEditingName(true); setEditName(user.name || ""); }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              data-testid="profile-name"
            >
              {user.name || <span style={{ color: '#8A8A9A', fontStyle: 'italic' }}>Tap to add name</span>}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A8A9A" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
          </>
        )}
        
        <div className="profile-contact">{user.email}</div>
      </div>

      {menuItems.map((item) => (
        <button key={item} className="profile-menu-item" onClick={() => handleMenuClick(item)} data-testid={`menu-${item.toLowerCase().replace(/\s/g, "-")}`}>
          <span>{item}</span>
          <span className="profile-chevron">›</span>
        </button>
      ))}

      {/* Export Health Report Button */}
      <button 
        className="profile-menu-item" 
        onClick={handleExportReport}
        disabled={exporting}
        style={{ 
          background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)',
          borderLeft: '3px solid #8B5CF6'
        }}
        data-testid="menu-export-report"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {exporting ? 'Generating Report...' : 'Export Health Report'}
        </span>
        <span className="profile-chevron">
          {exporting ? (
            <svg width="16" height="16" viewBox="0 0 24 24" className="spin">
              <circle cx="12" cy="12" r="10" stroke="#6D5EF5" strokeWidth="2" fill="none" strokeDasharray="32" strokeLinecap="round"/>
            </svg>
          ) : '↓'}
        </span>
      </button>

      {/* Privacy & Data Protection Section */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{ 
          fontSize: 12, 
          fontWeight: 700, 
          color: '#8A8A9A', 
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 12,
          paddingLeft: 4
        }}>
          Privacy & Data Protection
        </h3>
        
        <button 
          className="profile-menu-item" 
          onClick={() => setActiveModal('privacy')}
          data-testid="menu-privacy-policy"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Privacy Policy
          </span>
          <span className="profile-chevron">›</span>
        </button>
        
        <button 
          className="profile-menu-item" 
          onClick={() => setActiveModal('terms')}
          data-testid="menu-terms-of-use"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Terms of Use
          </span>
          <span className="profile-chevron">›</span>
        </button>
        
        <button 
          className="profile-menu-item" 
          onClick={() => setActiveModal('disclaimer')}
          data-testid="menu-medical-disclaimer"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6D5EF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Medical Disclaimer
          </span>
          <span className="profile-chevron">›</span>
        </button>
      </div>

      <button className="btn-ghost" onClick={onSignOut} style={{ marginTop: 24 }} data-testid="sign-out-btn">Sign Out</button>

      {/* Notification Debug Panel */}
      <div style={{ marginTop: 24 }}>
        <button
          onClick={async () => {
            if (!showDebug) {
              setDebugLoading(true);
              setTriggerResult(null);
              try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const resp = await fetch(`${backendUrl}/api/notifications/debug/${userId}`);
                const data = await resp.json();
                const subId = await getSubscriptionId();
                data.current_subscription_id = subId;
                setDebugData(data);
              } catch (e) {
                setDebugData({ error: e.message });
              }
              setDebugLoading(false);
            }
            setShowDebug(!showDebug);
          }}
          data-testid="notification-debug-toggle"
          style={{
            width: '100%', background: 'none', border: '1px solid #E5E7EB',
            borderRadius: 12, padding: '12px 16px', fontSize: 13, fontWeight: 500,
            color: '#5A5A5A', cursor: 'pointer', textAlign: 'left',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}
        >
          <span>Notification Debug</span>
          <span style={{ color: '#8A8A9A', fontSize: 12 }}>{showDebug ? 'Hide' : 'Show'}</span>
        </button>

        {showDebug && debugData && (
          <div style={{
            marginTop: 8, background: '#F9FAFB', borderRadius: 12,
            padding: 16, fontSize: 12, fontFamily: 'monospace'
          }} data-testid="notification-debug-panel">
            {debugData.error ? (
              <p style={{ color: '#DC2626' }}>Error: {debugData.error}</p>
            ) : (
              <>
                <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8A8A9A' }}>Player ID:</span>
                    <span style={{ color: debugData.player_id ? '#059669' : '#DC2626', fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {debugData.player_id || 'Not registered'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8A8A9A' }}>Current Sub ID:</span>
                    <span style={{ color: '#5A5A5A', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {debugData.current_subscription_id || 'N/A'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8A8A9A' }}>Test User:</span>
                    <span style={{
                      background: debugData.test_user ? '#D1FAE5' : '#FEE2E2',
                      color: debugData.test_user ? '#065F46' : '#991B1B',
                      padding: '2px 8px', borderRadius: 8, fontWeight: 600
                    }}>
                      {debugData.test_user ? 'YES' : 'NO'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8A8A9A' }}>Sent Today:</span>
                    <span>{debugData.notification_sent_today ? 'Yes' : 'No'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8A8A9A' }}>Symptoms Logged:</span>
                    <span>{debugData.symptoms_logged_today ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button
                    onClick={async () => {
                      const backendUrl = process.env.REACT_APP_BACKEND_URL;
                      await fetch(`${backendUrl}/api/notifications/mark-test-user`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user_id: userId, test_user: !debugData.test_user })
                      });
                      const resp = await fetch(`${backendUrl}/api/notifications/debug/${userId}`);
                      const data = await resp.json();
                      data.current_subscription_id = debugData.current_subscription_id;
                      setDebugData(data);
                    }}
                    data-testid="toggle-test-user-btn"
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
                      background: debugData.test_user ? '#FEE2E2' : '#D1FAE5',
                      color: debugData.test_user ? '#991B1B' : '#065F46',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {debugData.test_user ? 'Remove Test' : 'Mark as Test'}
                  </button>
                  <button
                    onClick={async () => {
                      setTriggerResult('sending...');
                      const backendUrl = process.env.REACT_APP_BACKEND_URL;
                      const resp = await fetch(`${backendUrl}/api/notifications/trigger-reminder`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ test_only: true })
                      });
                      const data = await resp.json();
                      setTriggerResult(JSON.stringify(data.results || data, null, 2));
                      // Refresh debug
                      const dbg = await fetch(`${backendUrl}/api/notifications/debug/${userId}`);
                      const dbgData = await dbg.json();
                      dbgData.current_subscription_id = debugData.current_subscription_id;
                      setDebugData(dbgData);
                    }}
                    data-testid="trigger-reminder-btn"
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
                      background: '#EDE9F8', color: '#6D5EF5',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Trigger Reminder
                  </button>
                </div>

                <button
                  onClick={async () => {
                    setTriggerResult('testing API key...');
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const resp = await fetch(`${backendUrl}/api/notifications/test-api-key`);
                    const data = await resp.json();
                    setTriggerResult(JSON.stringify(data, null, 2));
                  }}
                  data-testid="test-api-key-btn"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
                    background: '#fff', color: '#5A5A5A',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 12
                  }}
                >
                  Test OneSignal API Key
                </button>

                {triggerResult && (
                  <pre style={{
                    background: '#1F2937', color: '#10B981', borderRadius: 8,
                    padding: 10, fontSize: 10, overflow: 'auto', maxHeight: 120,
                    marginBottom: 12, whiteSpace: 'pre-wrap'
                  }}>
                    {triggerResult}
                  </pre>
                )}

                {/* Recent logs */}
                {debugData.recent_logs?.length > 0 && (
                  <>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#5A5A5A', marginBottom: 6 }}>Recent Logs:</p>
                    {debugData.recent_logs.map((log, i) => (
                      <div key={i} style={{
                        background: '#fff', borderRadius: 8, padding: '8px 10px',
                        marginBottom: 4, borderLeft: `3px solid ${log.status === 'sent' ? '#10B981' : log.status === 'skipped' ? '#F59E0B' : '#EF4444'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#8A8A9A' }}>
                          <span>{log.date}</span>
                          <span style={{
                            textTransform: 'uppercase', fontWeight: 700,
                            color: log.status === 'sent' ? '#10B981' : log.status === 'skipped' ? '#F59E0B' : '#EF4444'
                          }}>{log.status}</span>
                        </div>
                        <p style={{ fontSize: 10, color: '#5A5A5A', margin: '2px 0 0' }}>
                          {log.message || log.reason || '-'}
                        </p>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Legal Modal */}
      {activeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setActiveModal(null)}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            maxWidth: 400,
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #EEEAF8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)'
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'terms' && 'Terms of Use'}
                {activeModal === 'disclaimer' && 'Medical Disclaimer'}
              </h3>
              <button 
                onClick={() => setActiveModal(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: 18
                }}
                data-testid="close-modal-btn"
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{
              padding: 20,
              overflowY: 'auto',
              fontSize: 13,
              lineHeight: 1.7,
              color: '#374151'
            }}>
              {activeModal === 'privacy' && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {privacyPolicy.split('**').map((part, idx) => 
                    idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                  )}
                </div>
              )}
              {activeModal === 'terms' && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {termsOfUse.split('**').map((part, idx) => 
                    idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                  )}
                </div>
              )}
              {activeModal === 'disclaimer' && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {medicalDisclaimer.split('**').map((part, idx) => 
                    idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                  )}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #EEEAF8',
              background: '#F9FAFB'
            }}>
              <button 
                className="btn-primary"
                onClick={() => setActiveModal(null)}
                style={{ width: '100%' }}
                data-testid="acknowledge-btn"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <div className="toast-container"><div className="toast">{toastMessage}</div></div>
      )}

      {showMyReports && (
        <MyReportsModal userId={userId} onClose={() => setShowMyReports(false)} />
      )}
      
      {/* App Version */}
      <div style={{
        textAlign: 'center',
        marginTop: 24,
        paddingBottom: 100
      }}>
        <p style={{ 
          fontSize: 12, 
          color: '#9CA3AF',
          margin: 0
        }}>
          PCOS Insights
        </p>
        <p style={{ 
          fontSize: 11, 
          color: '#C4B5FD',
          margin: '4px 0 0 0',
          fontWeight: 500
        }} data-testid="app-version">
          Version {APP_VERSION}
        </p>
      </div>
    </div>
  );
}
